import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { adminApi } from '../lib/api'

interface AdminUser {
  id: string
  username: string
  email: string
  role: 'super_admin' | 'admin' | 'support'
}

interface AuthContextType {
  user: AdminUser | null
  isAuthenticated: boolean
  isLoading: boolean
  requestCode: (email: string) => Promise<{ success: boolean; session_token?: string; error?: string }>
  verifyCode: (email: string, code: string, sessionToken: string) => Promise<{ success: boolean; error?: string }>
  resendCode: (email: string, sessionToken: string) => Promise<{ success: boolean; error?: string }>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for existing session
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token')
      if (token) {
        try {
          adminApi.setToken(token)
          const response = await adminApi.getMe()
          if (response.success && response.user) {
            setUser(response.user as AdminUser)
          } else {
            // Invalid token
            localStorage.removeItem('admin_token')
            localStorage.removeItem('admin_user')
            adminApi.setToken(null)
          }
        } catch {
          // Token expired or invalid
          localStorage.removeItem('admin_token')
          localStorage.removeItem('admin_user')
          adminApi.setToken(null)
        }
      }
      setIsLoading(false)
    }
    checkAuth()
  }, [])

  const requestCode = async (email: string): Promise<{ success: boolean; session_token?: string; error?: string }> => {
    try {
      const response = await adminApi.requestCode(email)
      if (response.success) {
        return { success: true, session_token: response.session_token }
      }
      return { success: false, error: 'Failed to send code' }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to send code' }
    }
  }

  const verifyCode = async (email: string, code: string, sessionToken: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await adminApi.verifyCode(email, code, sessionToken)
      if (response.success && response.token && response.user) {
        adminApi.setToken(response.token)
        setUser(response.user as AdminUser)
        localStorage.setItem('admin_user', JSON.stringify(response.user))
        return { success: true }
      }
      return { success: false, error: 'Verification failed' }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Verification failed' }
    }
  }

  const resendCode = async (email: string, sessionToken: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await adminApi.resendCode(email, sessionToken)
      if (response.success) {
        return { success: true }
      }
      return { success: false, error: 'Failed to resend code' }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to resend code' }
    }
  }

  const logout = async () => {
    try {
      await adminApi.logout()
    } catch {
      // Ignore logout errors
    }
    setUser(null)
    adminApi.setToken(null)
    localStorage.removeItem('admin_user')
    localStorage.removeItem('admin_token')
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        requestCode,
        verifyCode,
        resendCode,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
